import * as React from 'react';
import { get, isEmpty, snakeCase } from 'lodash';
import classnames from 'classnames';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import Spinner from 'components/spinner/Spinner';

import RelatedInfluencerCard from '../related-influencer-card/RelatedInfluencerCard';

import { __t, applyTranslations } from 'utils/language';
import { isMinimumCriteriaSelected } from 'utils/influencers';

import './influencer-profile-related-influencers.scss';

import type { InfluencerTile } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./influencer-profile-related-influencers.i18n.json'));

const SCROLL_STEP: number = 304;
const MAX_CARDS_ON_GRID = 4;
const RELATED_CREATORS_SEARCH_TYPE = [
  'enableDemographicConditions',
  'enableAudienceConditions',
  'enableChannelConditions'
];

type Props = {
  relatedInfluencers: Array<InfluencerTile>,
  isLoading: boolean,
  relatedInfluencersSearchQuery: {
    enableDemographicConditions: boolean,
    enableAudienceConditions: boolean,
    enableChannelConditions: boolean
  },
  onRelatedInfluencerSearchQueryChange: (a: SyntheticEvent<HTMLInputElement>) => void
};

type State = {
  scrollPosition: number,
  canScroll: {
    left: boolean,
    right: boolean
  },
  diff: number,
  contentWidth: number,
  screenWidth: number
};

class InfluencerProfileRelatedInfluencers extends React.Component<Props, State> {
  content: HTMLDivElement | undefined | null = null;
  influencers: HTMLUListElement | undefined | null = null;
  state = {
    scrollPosition: 0,
    canScroll: {
      left: false,
      right: false
    },
    diff: 0,
    contentWidth: 0,
    screenWidth: 0
  };

  componentDidMount(): void {
    this.calcScroll(0);
    if (typeof window === 'object' && window.addEventListener) { // it's for Jest
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.relatedInfluencers.length !== this.props.relatedInfluencers.length) {
      this.calcScroll(0);
    }
  }

  componentWillUnmount(): void {
    if (typeof window === 'object' && window.removeEventListener) { // it's for Jest
      window.removeEventListener('resize', this.handleResize);
    }
  }

  calcScroll(position: number = 0): void {
    if (this.content && this.influencers) {
      const maxWidth = get(this.influencers, 'clientWidth', 0);
      const width = get(this.content, 'clientWidth', 0);
      const diff = Math.max(maxWidth - width, 0);
      const isScrollNeeded = maxWidth !== width;

      this.setState({
        scrollPosition: position,
        canScroll: {
          left: isScrollNeeded && position > 0,
          right: isScrollNeeded && (position < diff)
        },
        diff,
        contentWidth: maxWidth,
        screenWidth: width
      });

      if (this.influencers && this.influencers.style) {
        this.influencers.style.transform = `translateX(${-position}px)`;
      }
    }
  }

  calcStepSize(): number {
    const { screenWidth } = this.state;
    const cardsCount = Math.floor(screenWidth / SCROLL_STEP);

    return Math.max(cardsCount * SCROLL_STEP, SCROLL_STEP);
  }

  handleScrollLeft = (): void => {
    const { canScroll, scrollPosition } = this.state;

    if (canScroll.left) {
      this.calcScroll(Math.max(scrollPosition - this.calcStepSize(), 0));
    }
  };

  handleScrollRight = (): void => {
    const { canScroll, scrollPosition, diff } = this.state;

    if (canScroll.right) {
      this.calcScroll(Math.min(scrollPosition + this.calcStepSize(), diff));
    }
  };

  handleResize = (): void => {
    this.calcScroll(0);
  };

  render(): React.ReactNode {
    const { relatedInfluencers, isLoading, relatedInfluencersSearchQuery, onRelatedInfluencerSearchQueryChange } = this.props;
    const { contentWidth, screenWidth, canScroll, scrollPosition } = this.state;
    const hasMinimumCriteriaSelected = isMinimumCriteriaSelected(relatedInfluencersSearchQuery);

    const firstVisibleCardIndex = scrollPosition / SCROLL_STEP;

    const showInfluencers = !isEmpty(relatedInfluencers) && !isLoading && hasMinimumCriteriaSelected;
    const placeholder = hasMinimumCriteriaSelected ?
      __t('influencer_profile_related_influencers.influencers_not_found') :
      __t('influencer_profile_related_influencers.minimum_criteria_not_selected');

    const leftArrowClasses = classnames({
      'influencer-profile-related-influencers__arrow': true,
      'influencer-profile-related-influencers__arrow--left': true,
      'influencer-profile-related-influencers__arrow--disabled': !canScroll.left
    });

    const rightArrowClasses = classnames({
      'influencer-profile-related-influencers__arrow': true,
      'influencer-profile-related-influencers__arrow--right': true,
      'influencer-profile-related-influencers__arrow--disabled': !canScroll.right
    });

    return (
      <section className="influencer-profile-related-influencers">
        <div className="influencer-profile-related-influencers__heading">
          <div className="influencer-profile-related-influencers__title">
            <div className="influencer-profile-related-influencers__title-text">
              {__t('influencer_profile_related_influencers.title')}
            </div>
            <div className="influencer-profile-related-influencers__tooltip">
              <Tooltip
                message={__t('influencer_profile_related_influencers.tooltip')}
                showQuestionMark
              />
            </div>
          </div>
        </div>
        <div className="influencer-profile-related-influencers__search">
          <div className="influencer-profile-related-influencers__search--title">
            {__t('influencer_profile_related_influencers.similar_content_creator_search.title')}
          </div>
          {
            RELATED_CREATORS_SEARCH_TYPE.map(type => (
              <InputCheckbox
                name={type}
                key={type}
                label={__t(`influencer_profile_related_influencers.similar_content_creator_search.${snakeCase(type)}`)}
                id={`similar_content_creator_search--task-type-${type}`}
                size="small"
                checked={relatedInfluencersSearchQuery[type]}
                onChange={onRelatedInfluencerSearchQueryChange}
                tooltipMessage={__t(`influencer_profile_related_influencers.similar_content_creator_search.tooltips.${snakeCase(type)}`)}
              />
            ))
          }
        </div>
        <div className="influencer-profile-related-influencers__content-wrapper">
          {
            isLoading &&
            <div className="influencer-profile-related-influencers__spinner-container">
              <Spinner color="light" position="inline" size="small" />
            </div>
          }
          {
            (isEmpty(relatedInfluencers) || !hasMinimumCriteriaSelected) && !isLoading &&
            <div className="influencer-profile-related-influencers__influencer-not-found">
              {placeholder}
            </div>
          }
          {
            contentWidth > screenWidth && showInfluencers && (
              <React.Fragment>
                <div
                  className={leftArrowClasses}
                  onClick={this.handleScrollLeft}
                >
                  <SVGDynamicIcon
                    name="chevron-left-custom-fg"
                    width={12}
                    height={22}
                  />
                </div>
                <div
                  className={rightArrowClasses}
                  onClick={this.handleScrollRight}
                >
                  <SVGDynamicIcon
                    name="chevron-right-custom-fg"
                    width={12}
                    height={22}
                  />
                </div>
              </React.Fragment>
            )
          }
          {
            showInfluencers &&
            <div
              className="influencer-profile-related-influencers__content"
              ref={ref => { this.content = ref; }}
            >
              <ul
                className="influencer-profile-related-influencers__related-influencers"
                ref={ref => { this.influencers = ref; }}
              >
                {
                  relatedInfluencers.map((influencer, index) => (
                    <li
                      className="influencer-profile-related-influencers__related-influencer"
                      key={`related-influencer-card-${influencer.id}}`}
                    >
                      <RelatedInfluencerCard
                        shouldLoadImages={index >= firstVisibleCardIndex && index < (firstVisibleCardIndex + MAX_CARDS_ON_GRID)}
                        influencer={influencer}
                      />
                    </li>
                  ))
                }
              </ul>
            </div>
          }
        </div>
      </section>
    );
  }
}

export default InfluencerProfileRelatedInfluencers;
